import React, { useEffect, useState } from "react";
import StarIcon from "@mui/icons-material/Star";
import { teal } from "@mui/material/colors";
import { Button, Divider, Snackbar, Alert } from "@mui/material";
import {
  Add,
  AddShoppingCart,
  Favorite,
  LocalShipping,
  Remove,
  Shield,
  Wallet,
  WorkspacePremium,
} from "@mui/icons-material";
import SimilarProduct from "./SimilarProduct";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { fetchProductById } from "../../../State/customer/productSlice";
import { useParams } from "react-router-dom";
import { AddProductToWishList } from "../../../State/customer/wishlistSlice";
import { addItemToCart } from "../../../State/customer/cartSlice";

const FALLBACK_IMAGE =
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTctl02UiSUqaJlEHt9du_AiYsMlg_Uq4t0eQ&s";

const ProductDetails = () => {
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState<string>("M");
  const [activeImage, setActiveImage] = useState(0);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const dispatch = useAppDispatch();
  const { productId } = useParams();

  const { product, loading } = useAppSelector((state) => state.product);

  useEffect(() => {
    if (productId) {
      dispatch(fetchProductById(Number(productId)));
      setActiveImage(0);
      setQuantity(1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [productId, dispatch]);

  const handleActiveImage = (value: number) => () => {
    setActiveImage(value);
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleWishlist = () => {
    if (product?.id) {
      const jwt = localStorage.getItem("jwt");
      if (!jwt) {
        // Save guest wishlist in local storage
        const guestWishlist = JSON.parse(
          localStorage.getItem("guest_wishlist") || "[]"
        );
        const exists = guestWishlist.some((item: any) => item.id === product.id);
        if (!exists) {
          guestWishlist.push(product);
          localStorage.setItem("guest_wishlist", JSON.stringify(guestWishlist));
        }
        setSnackbarMessage("Added to wishlist successfully!");
        setSnackbarOpen(true);
        return;
      }

      dispatch(AddProductToWishList({ productId: product.id }))
        .unwrap()
        .then(() => {
          setSnackbarMessage("Added to wishlist successfully!");
          setSnackbarOpen(true);
        })
        .catch((error) => console.error("Wishlist error:", error));
    }
  };

  const handleAddToCart = () => {
    if (product?.id) {
      const jwt = localStorage.getItem("jwt");
      dispatch(
        addItemToCart({
          jwt: jwt,
          request: {
            productId: product.id,
            size: selectedSize,
            quantity: quantity,
            product: product, // Attached so client can render guest cart details without needing extra fetches
          },
        })
      )
        .unwrap()
        .then(() => {
          setSnackbarMessage("Product added to cart successfully!");
          setSnackbarOpen(true);
        })
        .catch((error) => console.error("Add to cart error:", error));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh] text-teal-600 font-medium">
        Loading product details...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex justify-center items-center min-h-[50vh] text-gray-500 font-medium">
        Product not found.
      </div>
    );
  }

  const availableSizes = product?.sizes
    ? product.sizes.split(",")
    : ["FREE", "S", "M", "L", "XL"];

  const images = product?.images?.length ? product.images : [FALLBACK_IMAGE];

  // Helper function to split description into bullet points
  const getFormattedDescriptionBullets = (description?: string) => {
    if (!description) return [];
    
    // Split on standard sentence endings or newlines, then clean whitespace
    return description
      .split(/(?<=[.!?])\s+|\n+/)
      .map((bullet) => bullet.trim())
      .filter((bullet) => bullet.length > 0);
  };

  const descriptionBullets = getFormattedDescriptionBullets(product?.description);

  return (
    <div className="px-4 sm:px-8 lg:px-20 py-6 lg:py-10 max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Images */}
        <section className="flex flex-col-reverse lg:flex-row gap-4 lg:gap-5">
          <div className="w-full lg:w-[15%] flex flex-row lg:flex-col gap-3 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 scrollbar-none">
            {images.map((item, index) => (
              <img
                key={index}
                onClick={handleActiveImage(index)}
                className={`w-[60px] h-[60px] sm:w-[75px] sm:h-[75px] lg:w-full lg:h-20 object-cover flex-shrink-0 cursor-pointer rounded-md border-2 transition-all ${
                  activeImage === index
                    ? "border-teal-600 shadow-sm opacity-100"
                    : "border-gray-200 opacity-70 hover:opacity-100 hover:border-teal-300"
                }`}
                src={item}
                alt={`Thumbnail ${index + 1}`}
              />
            ))}
          </div>

          <div className="w-full lg:w-[85%]">
            <img
              className="w-full rounded-lg object-cover max-h-[350px] sm:max-h-[480px] lg:max-h-[600px] shadow-sm border border-gray-100"
              src={images[activeImage] || images[0]}
              alt={product?.title || "Product image"}
            />
          </div>
        </section>

        {/* Details */}
        <section className="flex flex-col justify-between">
          <div>
            <h1 className="font-bold text-xl sm:text-2xl lg:text-3xl text-gray-800 leading-snug">
              {product?.title || "Product Title"}
            </h1>

            {/* Formatted Description in Bullets */}
            <div className="mt-3">
              <ul className="list-disc pl-5 space-y-1.5 text-gray-600 text-sm sm:text-base leading-relaxed">
                {descriptionBullets.map((bullet, index) => (
                  <li key={index}>{bullet}</li>
                ))}
              </ul>
            </div>

            <div className="flex items-center gap-3 py-1 border border-teal-100 bg-teal-50/50 rounded-md w-fit px-3 mt-4 text-xs sm:text-sm font-medium text-gray-700">
              <div className="flex gap-1 items-center">
                <span>4.0</span>
                <StarIcon sx={{ color: teal[600], fontSize: "18px" }} />
              </div>
              <Divider orientation="vertical" flexItem sx={{ borderColor: teal[100] }} />
              <span className="text-teal-900">234 Ratings</span>
            </div>

            <div className="mt-5">
              <div className="price flex items-baseline flex-wrap gap-3 text-xl sm:text-2xl lg:text-3xl">
                <span className="font-sans font-bold text-gray-900">
                  Ksh {product?.sellingPrice?.toLocaleString()}
                </span>
                {product?.mrpPrice > product?.sellingPrice && (
                  <>
                    <span className="line-through text-gray-400 text-lg sm:text-xl">
                      Ksh {product?.mrpPrice?.toLocaleString()}
                    </span>
                    <span className="text-teal-600 font-semibold text-base sm:text-lg">
                      {product?.discountPercent}% OFF
                    </span>
                  </>
                )}
              </div>
              <p className="text-xs sm:text-sm mt-1 text-gray-500">
                Inclusive of all taxes. Free shipping
              </p>
            </div>

            {/* Quantity */}
            <div className="mt-6 space-y-2">
              <h2 className="font-semibold text-xs sm:text-sm text-gray-700 tracking-wide uppercase">
                Quantity
              </h2>
              <div className="flex items-center gap-1 border border-teal-200 rounded-md w-fit bg-white">
                <Button
                  size="small"
                  disabled={quantity === 1}
                  onClick={() => setQuantity(quantity - 1)}
                  sx={{
                    minWidth: "40px",
                    color: teal[700],
                    "&:hover": { bgcolor: teal[50] },
                  }}
                >
                  <Remove fontSize="small" />
                </Button>
                <span className="font-semibold text-base px-3 text-gray-800">
                  {quantity}
                </span>
                <Button
                  size="small"
                  onClick={() => setQuantity(quantity + 1)}
                  sx={{
                    minWidth: "40px",
                    color: teal[700],
                    "&:hover": { bgcolor: teal[50] },
                  }}
                >
                  <Add fontSize="small" />
                </Button>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm text-gray-600">
              <div className="flex items-center gap-3">
                <Shield sx={{ color: teal[600], fontSize: 20 }} />
                <span>Authentic & Quality Assured</span>
              </div>
              <div className="flex items-center gap-3">
                <WorkspacePremium sx={{ color: teal[600], fontSize: 20 }} />
                <span>100% Money Back Guarantee</span>
              </div>
              <div className="flex items-center gap-3">
                <LocalShipping sx={{ color: teal[600], fontSize: 20 }} />
                <span>Free Shipping & Easy Returns</span>
              </div>
              <div className="flex items-center gap-3">
                <Wallet sx={{ color: teal[600], fontSize: 20 }} />
                <span>Pay on Delivery Available</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
            <Button
              onClick={handleAddToCart}
              fullWidth
              variant="contained"
              startIcon={<AddShoppingCart />}
              sx={{
                py: { xs: "12px", sm: "14px" },
                bgcolor: teal[600],
                "&:hover": { bgcolor: teal[700] },
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                boxShadow: "0 2px 4px rgba(13, 148, 136, 0.2)",
              }}
            >
              Add To Cart
            </Button>

            <Button
              onClick={handleWishlist}
              fullWidth
              variant="outlined"
              startIcon={<Favorite />}
              sx={{
                py: { xs: "12px", sm: "14px" },
                color: teal[700],
                borderColor: teal[600],
                "&:hover": {
                  borderColor: teal[700],
                  bgcolor: teal[50],
                },
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              Add To Wishlist
            </Button>
          </div>
        </section>
      </div>

      <div className="mt-12 sm:mt-16 border-t border-gray-100 pt-8">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
          Similar Products
        </h2>
        <div className="pt-4">
          <SimilarProduct />
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: "100%", bgcolor: teal[700], color: "#fff" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ProductDetails;